/**
 * The notation's state: the rows, which ply the board is showing, and the one
 * way to change it.
 *
 * Performance rules that shape this file (UX spec §6 rule 3):
 *  - history browsing is LOCAL ui state (`useV2Ui.viewPly`), never a store
 *    write, and the board picks it up through `boardPropsAtPly`;
 *  - `goTo` reads the live length out of the store instead of closing over
 *    `moves`, so appending a move does not hand every memoised <Ply> a new
 *    `onPress` and re-render the whole list (reviewer finding, M1a);
 *  - the keyboard listener is registered ONCE, reads the store with
 *    `getState()` and writes only the ui store, so it never needs re-binding.
 *
 * M4: which list the notation shows comes from `selectBoardSource`. In watch mode
 * the rows, the length and the ← → clamp all come from the watch slice — the
 * board, the list and the nav row have to agree about the length or browsing
 * would clamp against the wrong game.
 *
 * FIX F-C: the analysis branch rides alongside the main line. `branch` and
 * `current` come from `useAnalysis` — `plyCount`/`stepBranch` are exported so
 * `MoveNav` (the pinned ⏮ ◀ ▶ ⏭ row) shares the exact same rules rather than a
 * second copy that could drift.
 *
 * FIX F-D / D2: `hints` is the WHOLE assisted-game record (keyed by move_index,
 * 1-based), read with ONE extra `useShallow`'d subscription — it starts and
 * stays `{}` for every non-assisted game, so this costs nothing there (R4's
 * own invariant). `openHint` is `assistStore.open` plus the SAME `goTo` a
 * notation click already uses, so pressing "See hint…" both opens the card
 * and browses there in one call.
 */
import {useCallback, useEffect, useMemo} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {useLiveGame} from '@/Store/liveGameStore';
import type {LiveGameState} from '@/Store/liveGameStore';
import {
  selectBoardSource,
  selectMoves,
  selectWatchMoves,
} from '@/Store/liveGame/selectors';

import {useV2Ui} from '../../../theme/useV2Ui';
import {Hint, useAssistStore} from '../../assist/assistStore';
import {AnalysisStore, useAnalysis} from '../../analysis/analysisStore';

/**
 * One numbered line of the notation. The two SANs come with their OWN
 * `move_index` (1-based, "after N plies") rather than leaving the reader to
 * derive it from `number`: a game that starts with BLACK to move (a library
 * position — `game.isBlackTurnFirst`) offsets the whole list by one, so the
 * white column of row 1 is empty and `moves[0]` sits in the black column.
 */
export type MoveRow = {
  number: number;
  white?: string;
  black?: string;
  /** move_index of the SAN in the white column (1-based), if there is one */
  whitePly?: number;
  /** move_index of the SAN in the black column (1-based), if there is one */
  blackPly?: number;
};

/** how many plies the board is browsing against — MY game's, or the watched one. */
export const plyCount = (s: LiveGameState): number =>
  selectBoardSource(s) === 'watch' ? s.watch.moves.length : s.game.moves.length;

/**
 * Did the game on the board START with Black to move? A library position can
 * (`reducers/game.ts` derives `game.isBlackTurnFirst` from the start FEN's
 * side-to-move field the same way), and the notation then has to leave the
 * first white cell empty — `moves[0]` is Black's. The WATCHED game has no such
 * flag on its slice, so it is read off its own `startFen`, which is the field
 * the core derived the live one from.
 */
export const selectBlackFirst = (s: LiveGameState): boolean =>
  selectBoardSource(s) === 'watch'
    ? (s.watch.startFen ?? '').split(' ')[1] === 'b'
    : s.game.isBlackTurnFirst;

/** the branch row under the notation: its base ply, its SAN moves, and which
 * one is on the board (F-C / C5). */
export type MoveListBranch = {basePly: number; moves: string[]; cursor: number};

export type MoveListModel = {
  rows: MoveRow[];
  /** the game started with BLACK to move, so row 1 has no white move */
  blackFirst: boolean;
  /** how many plies are on the board right now (live length, or the browsed
   * ply) — the branch's `basePly` while one is open, so the main-line
   * highlight stays on the move it branched from */
  current: number;
  total: number;
  goTo: (ply: number) => void;
  branch: MoveListBranch | null;
  /** the branch row's own move press: jump the cursor (1-based) */
  setBranchCursor: (cursor: number) => void;
  /** the branch row's "× Back to game" chip */
  backToGame: () => void;
  /** fix F-D / D2: the assisted-game hints, keyed by move_index (1-based) */
  hints: Record<number, Hint>;
  /** "See hint…": open the card for this ply AND browse the board there */
  openHint: (moveIndex: number) => void;
};

/** ← → Home End, clamped into [0, total]; `total` means "follow the live game". */
const plyFor = (key: string, at: number, total: number): number => {
  if (key === 'ArrowLeft') {
    return at - 1;
  }
  if (key === 'ArrowRight') {
    return at + 1;
  }
  return key === 'Home' ? 0 : total;
};

const clamp = (ply: number, total: number): number =>
  ply < 0 ? 0 : ply > total ? total : ply;

const selectBranch = (s: AnalysisStore) => ({on: s.on, branch: s.branch});

/**
 * ← / → while a branch is open (F-C / C5): step its cursor instead of the
 * browsed ply. Cursor 0 on ← clears the branch and falls back to `goTo`'s
 * ordinary one-ply-back jump; → always steps — `branchStep`'s own clamp is a
 * no-op at the tip. False (nothing handled here) with no branch, so the
 * caller runs its usual ply arithmetic. Shared with `MoveNav`.
 */
export const stepBranch = (
  delta: -1 | 1,
  goTo: (ply: number) => void,
): boolean => {
  const branch = useAnalysis.getState().branch;
  if (!branch) {
    return false;
  }
  if (delta === 1 || branch.cursor > 0) {
    useAnalysis.getState().branchStep(delta);
  } else {
    useAnalysis.getState().clearBranch();
    goTo(branch.basePly - 1);
  }
  return true;
};

/**
 * The numbered rows. White-first is the plain pairing (moves 0/1 into row 1).
 * BLACK-FIRST offsets everything by one: row 1 carries only `moves[0]` in its
 * BLACK column, and every later row pairs an odd index with the even one after
 * it — which is exactly what `rail/assist/moveNumber.ts`'s own
 * `getChessMoveNumber` numbers a black-first game as ("1…", "2.", "2…", "3.").
 */
export const buildRows = (moves: string[], blackFirst: boolean): MoveRow[] => {
  const out: MoveRow[] = [];
  if (blackFirst) {
    if (moves.length > 0) {
      out.push({number: 1, black: moves[0], blackPly: 1});
    }
    for (let i = 1; i < moves.length; i += 2) {
      out.push({
        number: (i + 1) / 2 + 1,
        white: moves[i],
        whitePly: i + 1,
        black: moves[i + 1],
        blackPly: moves[i + 1] === undefined ? undefined : i + 2,
      });
    }
    return out;
  }
  for (let i = 0; i < moves.length; i += 2) {
    out.push({
      number: i / 2 + 1,
      white: moves[i],
      whitePly: i + 1,
      black: moves[i + 1],
      blackPly: moves[i + 1] === undefined ? undefined : i + 2,
    });
  }
  return out;
};

/**
 * Which ROW holds a given ply — what the View scrolls to. Black-first shifts
 * the whole list down by one half-move (`buildRows`).
 */
export const rowIndexFor = (ply: number, blackFirst: boolean): number =>
  Math.max(0, blackFirst ? Math.floor(ply / 2) : Math.ceil(ply / 2) - 1);

export function useMoveListModel(): MoveListModel {
  const source = useLiveGame(selectBoardSource);
  const moves = useLiveGame(
    source === 'watch' ? selectWatchMoves : selectMoves,
  );
  const blackFirst = useLiveGame(selectBlackFirst);
  const viewPly = useV2Ui(s => s.viewPly);
  const setViewPly = useV2Ui(s => s.setViewPly);
  const {branch: branchRaw} = useAnalysis(useShallow(selectBranch));

  const branch = useMemo<MoveListBranch | null>(
    () =>
      branchRaw
        ? {
            basePly: branchRaw.basePly,
            moves: branchRaw.moves.map(m => m.san),
            cursor: branchRaw.cursor,
          }
        : null,
    [branchRaw],
  );

  const current = branch
    ? branch.basePly
    : viewPly === null
    ? moves.length
    : viewPly;

  const goTo = useCallback(
    (ply: number) => {
      // a click on a main-line move always clears the branch first (C5)
      if (useAnalysis.getState().branch) {
        useAnalysis.getState().clearBranch();
      }
      const total = plyCount(useLiveGame.getState());
      const clamped = clamp(ply, total);
      setViewPly(clamped >= total ? null : clamped);
    },
    [setViewPly],
  );

  const setBranchCursor = useCallback((cursor: number) => {
    useAnalysis.getState().setBranchCursor(cursor);
  }, []);

  const backToGame = useCallback(() => {
    const leaving = useAnalysis.getState().branch;
    useAnalysis.getState().clearBranch();
    if (leaving) {
      const total = plyCount(useLiveGame.getState());
      setViewPly(leaving.basePly >= total ? null : leaving.basePly);
    }
  }, [setViewPly]);

  // fix F-D / D2: `{}` for every non-assisted game, forever — the same zero-
  // cost shape `branch` already is for a live game with the engine off
  const hints = useAssistStore(s => s.hints);
  const openHint = useCallback(
    (moveIndex: number) => {
      useAssistStore.getState().open(moveIndex);
      goTo(moveIndex);
    },
    [goTo],
  );

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    const onKey = (event: any) => {
      const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
      if (!keys.includes(event.key)) {
        return;
      }
      event.preventDefault();
      if (event.key === 'ArrowLeft' && stepBranch(-1, goTo)) {
        return;
      }
      if (event.key === 'ArrowRight' && stepBranch(1, goTo)) {
        return;
      }
      const total = plyCount(useLiveGame.getState());
      const at = useV2Ui.getState().viewPly ?? total;
      goTo(plyFor(event.key, at, total));
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [goTo]);

  const rows = useMemo(() => buildRows(moves, blackFirst), [moves, blackFirst]);

  return {
    rows,
    blackFirst,
    current,
    total: moves.length,
    goTo,
    branch,
    setBranchCursor,
    backToGame,
    hints,
    openHint,
  };
}
